import { GetThemedStylesByKey, StylesByKey } from '@/styles/types/utils';
import styled from '@mui/material/styles/styled';
import { ButtonProps } from './Button';

export type StyledProps = ButtonProps & {
  hasIconOnly: boolean;
};

const sizeStyles: StylesByKey<StyledProps['size']> = {
  extra: {
    height: 24,
  },
  small: {
    height: 32,
  },
  medium: {
    height: 40,
  },
  large: {
    height: 48,
  },
};

const widthBySize: StylesByKey<StyledProps['width']> = {
  extraSmall: {
    width: '40px',
  },
  small: {
    width: '96px',
  },
  medium: {
    width: '160px',
  },
  fluid: {
    width: '100%',
  },
  auto: {
    width: 'auto',
  },
};

const getVariantStyles: GetThemedStylesByKey<StyledProps['variant']> = ({ palette, typography }) => ({
  primary: {
    backgroundColor: palette.primary.main,
    color: palette.other.white,
    '&:hover': {
      backgroundColor: palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: palette.button.disabled,
      color: palette.other.white,
    },
  },
  secondary: {
    backgroundColor: palette.button.lightBackground,
    borderColor: palette.button.border,
    borderStyle: 'solid',
    borderWidth: 1,
    color: palette.primary.main,
    '&:hover': {
      color: palette.secondary.main,
      backgroundColor: palette.secondary.light,
      borderColor: palette.secondary.deep,
    },
    '&:disabled': {
      color: palette.text.grey,
      backgroundColor: palette.other.white,
      borderColor: palette.border.whiteInverted,
    },
  },
  secondaryError: {
    backgroundColor: palette.other.errorBackground,
    borderColor: palette.border.redLight,
    borderStyle: 'solid',
    borderWidth: 1,
    color: palette.information.red,
    '&:hover': {
      color: palette.information.red,
      backgroundColor: palette.border.redLight,
      borderColor: palette.border.redLight,
    },
    '&:disabled': {
      color: palette.text.grey,
      backgroundColor: palette.other.white,
      borderColor: palette.border.whiteInverted,
    },
  },
  link: {
    backgroundColor: 'transparent',
    color: palette.primary.main,
    '&:hover': {
      backgroundColor: palette.button.lightBackground,
    },
    '&:disabled': {
      backgroundColor: palette.other.white,
      color: palette.text.grey,
    },
  },
  additionalLinkPrimary: {
    backgroundColor: palette.other.white,
    color: palette.primary.main,
    ...typography.overline,
    '&:hover': {
      backgroundColor: palette.button.lightBackground,
    },
    '&:disabled': {
      backgroundColor: palette.other.white,
      color: palette.button.disabled,
    },
  },
  additionalLinkSecondary: {
    backgroundColor: palette.other.white,
    color: palette.text.secondary,
    ...typography.overline,
    '&:hover': {
      color: palette.primary.main,
      backgroundColor: palette.other.white,
    },
    '&:disabled': {
      backgroundColor: palette.other.white,
      color: palette.button.disabled,
    },
  },
  round: {
    backgroundColor: palette.tags.greyLight,
    color: palette.tags.greyDark,
    borderRadius: 29,
    '&:hover': {
      backgroundColor: palette.other.informationLight,
    },
    '&:disabled': {
      backgroundColor: palette.button.disabled,
      color: palette.other.white,
    },
  },
});

const iconOnlyStylesBySize: StylesByKey<StyledProps['size']> = {
  extra: {
    height: 24,
    width: 24,
  },
  small: {
    height: 32,
    width: 32,
  },
  medium: {
    height: 40,
    width: 40,
  },
  large: {
    height: 48,
    width: 48,
  },
};

export const StyledButton = styled('button')<StyledProps>`
  cursor: pointer;
  border: none;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  white-space: nowrap;

  :disabled {
    cursor: default;
  }

  ${({ theme, hasIconOnly, variant = 'primary', width = 'medium', size = 'medium', typographyStyle }) => ({
    ...widthBySize[width],
    ...theme.typography.button,
    ...getVariantStyles(theme)[variant],
    ...(hasIconOnly ? { ...iconOnlyStylesBySize[size] } : { ...sizeStyles[size] }),
    ...(hasIconOnly && { padding: 0 }),
    ...(typographyStyle && theme.typography[typographyStyle]),
  })}
`;
